// listSummary.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MainTableItem, BottomsMainItem, useApiData, NglSchedule } from '../../../../contexts/apiService';
import './listSummary.css'

interface SummaryListProps {
    mainViewSelected: string;
    mainTableData: MainTableItem[] | BottomsMainItem[] | NglSchedule[] | undefined;
    summaryData: MainTableItem[] | undefined;
    onSelectedProducersChange?: (selectedProducers: string[]) => void;
    onSelectedDestinationsChange?: (selectedDestinations: string[]) => void;
    selectedMainView: string;
    bottomsData: BottomsMainItem[] | undefined;
    nglData: NglSchedule[] | undefined;
    selectedProduct: string;
    selectedMonthYear: string;
}

type FormatType = 'number' | 'currency' | 'accounting';

interface FormattingConfig {
    type: FormatType;
    decimals?: number;
}

// Create the formatting function
const formatValue = (value: number, config: FormattingConfig): string | JSX.Element => {
    const decimals = config.decimals ?? 2;
    const formattedNumber = value.toLocaleString('en-US', { 
        minimumFractionDigits: decimals, 
        maximumFractionDigits: decimals 
    });

    switch (config.type) {
        case 'number':
            return formattedNumber;
        case 'currency':
            return `$ ${formattedNumber}`;
        case 'accounting':
            // Return JSX for accounting format with separate alignment
            return (
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <span style={{ display: 'flex', paddingLeft: '2px' }}>$</span>
                    <span style={{ display: 'flex', flex: '1', justifyContent: 'right', paddingRight: '5px' }}>{formattedNumber}</span>
                </div>
            );
        default:
            return formattedNumber;
    }
};

// Configuration for adding columns
interface AggregationConfig {
    key: keyof MainTableItem | keyof BottomsMainItem | keyof NglSchedule | 'margin' | 'blend' | 'exposure' | 'quantity';
    label: string;
    formatting?: FormattingConfig;
    aggregationType: 'sum' | 'avg' | 'count' | 'min' | 'max' | 'calculated';
    alignment?: 'left' | 'right' | 'center';
    headerAlignment?: 'left' | 'right' | 'center';
    width?: string;
}

// base columns that are shared
const BASE_COLUMNS: AggregationConfig[] = [
    {
        key: 'gross_barrels',
        label: 'Gross Barrels',
        formatting: { type: 'number', decimals: 2 },
        aggregationType: 'sum',
        headerAlignment: 'left',
        alignment: 'right',
        width: '100px',
    },
    {
        key: 'net_barrels',
        label: 'Net Barrels',
        formatting: { type: 'number', decimals: 2 },
        aggregationType: 'sum',
        headerAlignment: 'left',
        alignment: 'right',
        width: '100px',
    },
    
];

const PRODUCER_SPECIFIC_COLUMNS: AggregationConfig[] = [
    {
        key: 'net_profit',
        label: 'Est. Gross Profit',
        formatting: { type: 'accounting', decimals: 2 },
        aggregationType: 'sum',
        headerAlignment: 'center',
        alignment: 'right',
        width: '120px',
    },
    {
        key: 'margin',
        label: 'Margin',
        formatting: { type: 'currency', decimals: 2 },
        aggregationType: 'calculated',
        alignment: 'center',
        width: '80px',
    },
    {
        key: 'total_purchase',
        label: 'Total Cost',
        formatting: { type: 'accounting', decimals: 2 },
        aggregationType: 'sum',
        headerAlignment: 'center',
        alignment: 'right',
        width: '130px',
    },
];

const DESTINATION_SPECIFIC_COLUMNS: AggregationConfig[] = [
    {
        key: 'exposure',
        label: 'Exposure',
        formatting: { type: 'accounting', decimals: 2 },
        aggregationType: 'calculated',
        headerAlignment: 'center',
        alignment: 'right',
        width: '130px',
    },
    {
        key: 'blend',
        label: 'Blend',
        formatting: { type: 'number', decimals: 2 },
        aggregationType: 'calculated',
        headerAlignment: 'center',
        alignment: 'center',
    },
];

const BOTTOMS_SPECIFIC_COLUMNS: AggregationConfig[] = [
    {
        key: 'Quantity',
        label: 'Gross Volume',
        formatting: { type: 'number', decimals: 2 },
        aggregationType: 'sum',
        headerAlignment: 'center',
        alignment: 'right',
        width: '130px',
    },
    {
        key: 'Net_Barrels',
        label: 'Net Barrels',
        formatting: { type: 'number', decimals: 2 },
        aggregationType: 'sum',
        headerAlignment: 'left',
        alignment: 'right',
        width: '100px',
    },
    {
        key: 'net_profit',
        label: 'Est. Gross Profit',
        formatting: { type: 'accounting', decimals: 2 },
        aggregationType: 'sum',
        headerAlignment: 'center',
        alignment: 'right',
        width: '130px',
    },
];

const NGL_SPECIFIC_COLUMNS: AggregationConfig[] = [
    {
        key: 'quantity',
        label: 'Est. Gallons',
        formatting: { type: 'number', decimals: 2 },
        aggregationType: 'sum',
        headerAlignment: 'center',
        alignment: 'right',
        width: '130px',
    },
    {
        key: 'net_profit',
        label: 'Est. Gross Profit',
        formatting: { type: 'accounting', decimals: 2 },
        aggregationType: 'sum',
        headerAlignment: 'center',
        alignment: 'right',
        width: '130px',
    },
];

const getAggregationColumns = (viewType: string, selectedProduct: string): AggregationConfig[] => {
    if (selectedProduct === 'tank_bottoms') {
        return [...BOTTOMS_SPECIFIC_COLUMNS];
    }

    if (selectedProduct === 'ngl') {
        return [...NGL_SPECIFIC_COLUMNS];
    }
    
    const baseColumns = [...BASE_COLUMNS];
    
    if (viewType === 'producer') {
        return [...baseColumns, ...PRODUCER_SPECIFIC_COLUMNS];
    } else if (viewType === 'destination' || viewType === 'market') {
        return [...baseColumns, ...DESTINATION_SPECIFIC_COLUMNS];
    }
    
    return baseColumns;
};

interface ListSummaryData {
    groupedData: string;
    aggregations: Record<string, number>;
}

const SummaryList = ({ 
    mainViewSelected,
    mainTableData, 
    summaryData, 
    onSelectedProducersChange,
    onSelectedDestinationsChange,
    selectedMainView,
    bottomsData,
    nglData,
    selectedProduct,
    selectedMonthYear,
}: SummaryListProps) => {

    const [selectedProducers, setSelectedProducers] = useState<string[]>([]);
    const [selectedDestinations, setSelectedDestinations] = useState<string[]>([]);
    const { apiData } = useApiData();

    const AGGREGATION_COLUMNS = useMemo(() => 
        getAggregationColumns(mainViewSelected, selectedProduct), 
        [mainViewSelected, selectedProduct]
    );

    // Helper function to check if row should be EXCLUDED from exposure calculation
    const shouldExcludeInExposure = useCallback((producer: string, station: string): boolean => {
        if (!apiData?.producer_pricing || !producer || !station) {
            return false; // Don't exclude if we can't determine
        }
        
        // Find all producer records with main = 1
        const producerRecords = apiData.producer_pricing.filter((record: any) => 
            record.producer === producer && record.main === 1
        );
        
        if (producerRecords.length === 0) {
            return false; // Don't exclude if no producer record found
        }
        
        // Month name to number mapping for comparison
        const monthToNumber = {
            'January': 1, 'February': 2, 'March': 3, 'April': 4,
            'May': 5, 'June': 6, 'July': 7, 'August': 8,
            'September': 9, 'October': 10, 'November': 11, 'December': 12
        };
        
        // Get the record with the most recent month/year
        const producerRecord = producerRecords.reduce((latest: any, current: any) => {
            const latestYear = Number(latest.year) || 0;
            const currentYear = Number(current.year) || 0;
            
            // Compare years first
            if (currentYear > latestYear) return current;
            if (currentYear < latestYear) return latest;
            
            // If years are equal, compare months
            const latestMonth = monthToNumber[latest.month as keyof typeof monthToNumber] || 0;
            const currentMonth = monthToNumber[current.month as keyof typeof monthToNumber] || 0;
            
            return currentMonth > latestMonth ? current : latest;
        });
        
        // EXCLUDE if producer has rockbridge_title = 0 AND station is in split_stations
        if (producerRecord.rockbridge_title === 0) {
            // Check if station is in split_stations JSON
            if (!producerRecord.split_stations) {
                return false; // No split_stations, so don't exclude
            }
            
            try {
                const splitStations = JSON.parse(producerRecord.split_stations);
                if (!Array.isArray(splitStations)) {
                    return false; // Invalid split_stations, so don't exclude
                }
                
                const isInSplitStations = splitStations.includes(station);
                
                return isInSplitStations; // Exclude if station is in split_stations
            } catch (error) {
                console.warn('Error parsing split_stations JSON:', error, { producer, split_stations: producerRecord.split_stations });
                return false; // Don't exclude on parse error
            }
        }
        
        return false; // Don't exclude if rockbridge_title is not 0
    }, [apiData]);

    const calculateAggregationsForMainTable = useCallback((items: MainTableItem[]): Record<string, number> => {
        const aggregations: Record<string, number> = {};
        
        AGGREGATION_COLUMNS.forEach(config => {
            if (config.key === 'margin') {
                // Calculate margin: total net_profit / total net_barrels
                const totalNetProfit = items
                    .map(item => Number(item.net_profit) || 0)
                    .reduce((sum, val) => sum + val, 0);
                
                const totalNetBarrels = items
                    .map(item => Number(item.net_barrels) || 0)
                    .reduce((sum, val) => sum + val, 0);
                
                aggregations[config.key as string] = totalNetBarrels !== 0 
                    ? totalNetProfit / totalNetBarrels 
                    : 0;
            } else if (config.key === 'blend') {
                // Calculate blend: sum(corrected_gravity * net_barrels) / sum(net_barrels)
                const weightedGravitySum = items
                    .map(item => {
                        const gravity = Number(item.corrected_gravity) || 0;
                        const barrels = Number(item.net_barrels) || 0;
                        return gravity * barrels;
                    })
                    .reduce((sum, val) => sum + val, 0);
                
                const totalNetBarrels = items
                    .map(item => Number(item.net_barrels) || 0)
                    .reduce((sum, val) => sum + val, 0);
                
                aggregations[config.key as string] = totalNetBarrels !== 0 
                    ? weightedGravitySum / totalNetBarrels 
                    : 0;
            } else if (config.key === 'exposure') {
                // Calculate exposure - EXCLUDE rows where:
                // Producer has rockbridge_title = 0 AND station is in split_stations
                const totalSale = items
                    .filter(item => {
                        const producer = item.producer || '';
                        const station = item.destination || '';
                        
                        // Return true to INCLUDE (i.e., don't exclude)
                        return !shouldExcludeInExposure(producer, station);
                    })
                    .map(item => Number(item.total_sale) || 0)
                    .reduce((sum, val) => sum + val, 0);
                
                // Add trucking difference for all rows (regardless of shouldExcludeInExposure filter)
                const truckingDifference = items
                    .map(item => (Number(item.trucking) || 0) - (Number(item.trucking_net) || 0))
                    .reduce((sum, val) => sum + val, 0);

                aggregations[config.key as string] = totalSale + truckingDifference;
                
            } else {
                const values = items
                    .map(item => Number(item[config.key as keyof MainTableItem]) || 0)
                    .filter(value => !isNaN(value));
                
                switch (config.aggregationType) {
                    case 'sum':
                        aggregations[config.key as string] = values.reduce((sum, val) => sum + val, 0);
                        break;
                    case 'avg':
                        aggregations[config.key as string] = values.length > 0 
                            ? values.reduce((sum, val) => sum + val, 0) / values.length 
                            : 0;
                        break;
                    case 'count':
                        aggregations[config.key as string] = values.length;
                        break;
                    case 'min':
                        aggregations[config.key as string] = values.length > 0 ? Math.min(...values) : 0;
                        break;
                    case 'max':
                        aggregations[config.key as string] = values.length > 0 ? Math.max(...values) : 0;
                        break;
                }
            }
        });
        
        return aggregations;
    }, [AGGREGATION_COLUMNS, shouldExcludeInExposure]);

    const calculateAggregationsForBottoms = useCallback((items: BottomsMainItem[]): Record<string, number> => {
        const aggregations: Record<string, number> = {};
        
        AGGREGATION_COLUMNS.forEach(config => {
            
            const values = items
                .map(item => Number(item[config.key as keyof BottomsMainItem]) || 0)
                .filter(value => !isNaN(value));
            
            switch (config.aggregationType) {
                case 'sum':
                    aggregations[config.key as string] = values.reduce((sum, val) => sum + val, 0);
                    break;
                case 'avg':
                    aggregations[config.key as string] = values.length > 0 
                        ? values.reduce((sum, val) => sum + val, 0) / values.length 
                        : 0;
                    break;
                case 'count':
                    aggregations[config.key as string] = values.length;
                    break;
                case 'min':
                    aggregations[config.key as string] = values.length > 0 ? Math.min(...values) : 0;
                    break;
                case 'max':
                    aggregations[config.key as string] = values.length > 0 ? Math.max(...values) : 0;
                    break;
            }
        });
        
        return aggregations;
    }, [AGGREGATION_COLUMNS]);

    const calculateAggregationsForNGL = useCallback((items: NglSchedule[]): Record<string, number> => {
        const aggregations: Record<string, number> = {};
        
        AGGREGATION_COLUMNS.forEach(config => {
            
            const values = items
                .map(item => Number(item[config.key as keyof NglSchedule]) || 0)
                .filter(value => !isNaN(value));
            
            switch (config.aggregationType) {
                case 'sum':
                    aggregations[config.key as string] = values.reduce((sum, val) => sum + val, 0);
                    break;
                case 'avg':
                    aggregations[config.key as string] = values.length > 0 
                        ? values.reduce((sum, val) => sum + val, 0) / values.length 
                        : 0;
                    break;
                case 'count':
                    aggregations[config.key as string] = values.length;
                    break;
                case 'min':
                    aggregations[config.key as string] = values.length > 0 ? Math.min(...values) : 0;
                    break;
                case 'max':
                    aggregations[config.key as string] = values.length > 0 ? Math.max(...values) : 0;
                    break;
            }
        });
        
        return aggregations;
    }, [AGGREGATION_COLUMNS]);

    // Reset both arrays to blank when changing between producer/destinations view
    useEffect(() => {
        setSelectedProducers([]);
        setSelectedDestinations([]);
        
        // Notify parent components of the reset
        setTimeout(() => {
            onSelectedProducersChange?.([]);
            onSelectedDestinationsChange?.([]);
        }, 0);
    }, [selectedMainView]);
    
    const listSummaryData = useMemo(() => {
        if (selectedProduct === 'tank_bottoms') {
            if (!bottomsData) return [];

            // Group bottoms data by the selected field
            const groupingField = mainViewSelected === 'producer' ? 'Operator' : mainViewSelected;
            const groups = bottomsData.reduce((acc, item) => {
                const fieldValue = item[groupingField as keyof BottomsMainItem];
                if (fieldValue && fieldValue !== '') {
                    if (!acc[fieldValue]) {
                        acc[fieldValue] = [];
                    }
                    acc[fieldValue].push(item);
                }
                return acc;
            }, {} as Record<string, BottomsMainItem[]>);;

            // Add selected items that might not be in current filtered view
            if (selectedMainView === "producer") {
                selectedProducers.forEach(selectedItem => {
                    if (!groups[selectedItem]) {
                        groups[selectedItem] = [];
                    }
                });
            }

            // Calculate aggregations for each grouped item
            const groupsWithAggregations: ListSummaryData[] = Object.entries(groups)
                .map(([groupValue, items]) => ({
                    groupedData: groupValue,
                    aggregations: calculateAggregationsForBottoms(items)
                }))
                .sort((a, b) => a.groupedData.localeCompare(b.groupedData));

            return groupsWithAggregations;
        }

        if (selectedProduct === 'ngl') {
            if (!nglData) return [];

            // Group bottoms data by the selected field
            const groupingField = mainViewSelected === 'producer' ? 'customer' : 'marketDestination';
            const groups = nglData.reduce((acc, item) => {
                const fieldValue = item[groupingField as keyof NglSchedule];
                if (fieldValue && fieldValue !== '') {
                    const keyValue = String(fieldValue);
                    if (!acc[keyValue]) {
                        acc[keyValue] = [];
                    }
                    acc[keyValue].push(item);
                }
                return acc;
            }, {} as Record<string, NglSchedule[]>);;

            // Add selected items that might not be in current filtered view
            if (selectedMainView === "producer") {
                selectedProducers.forEach(selectedItem => {
                    if (!groups[selectedItem]) {
                        groups[selectedItem] = [];
                    }
                });
            }

            // Calculate aggregations for each grouped item
            const groupsWithAggregations: ListSummaryData[] = Object.entries(groups)
                .map(([groupValue, items]) => ({
                    groupedData: groupValue,
                    aggregations: calculateAggregationsForNGL(items)
                }))
                .sort((a, b) => a.groupedData.localeCompare(b.groupedData));

            return groupsWithAggregations;
        }

        if (!mainTableData || !summaryData) return [];

        // Group data by the selected field
        const groups = summaryData.reduce((acc, item) => {
            const fieldValue = item[mainViewSelected as keyof MainTableItem];
            if (fieldValue && fieldValue !== '') {
                if (!acc[fieldValue]) {
                    acc[fieldValue] = [];
                }
                acc[fieldValue].push(item);
            }
            return acc;
        }, {} as Record<string, MainTableItem[]>);
        
        // if statements add selected items that might not be in current filtered view (status or timeframe)
        if (selectedMainView === "producer") {
            selectedProducers.forEach(selectedItem => {
                if (!groups[selectedItem]) {
                    groups[selectedItem] = [];
                }
            });
        }
        if (selectedMainView === "destination" || selectedMainView === 'market') {
            selectedDestinations.forEach(selectedItem => {
                if (!groups[selectedItem]) {
                    groups[selectedItem] = [];
                }
            });
        }

        // Calculate aggregations for each grouped item
        const groupsWithAggregations: ListSummaryData[] = Object.entries(groups)
            .map(([groupValue, items]) => ({
                groupedData: groupValue,
                aggregations: calculateAggregationsForMainTable(items)
            }))
            .sort((a, b) => a.groupedData.localeCompare(b.groupedData));

        return groupsWithAggregations;
    }, [mainTableData, summaryData, bottomsData, nglData, selectedProduct, mainViewSelected, selectedProducers, selectedDestinations, calculateAggregationsForMainTable, calculateAggregationsForBottoms]);

    // Subtotals calculation
    const subtotals = useMemo(() => {
        if (selectedProduct === 'tank_bottoms') {
            if (!bottomsData) return {};
            return calculateAggregationsForBottoms(bottomsData);
        }

        if (selectedProduct === 'ngl') {
            if (!nglData) return {};
            return calculateAggregationsForNGL(nglData);
        }
        
        if (selectedProduct === 'crude_oil') {
            if (!mainTableData) return {};
            return calculateAggregationsForMainTable(mainTableData as MainTableItem[]);
        }
    }, [selectedProduct, mainTableData, bottomsData, nglData, calculateAggregationsForMainTable, calculateAggregationsForBottoms]);

    // Handle summary row click
    const handleSummaryClick = useCallback((rowValue: string) => {
        if (mainViewSelected === 'producer') {
            setSelectedProducers(prev => {
                const newSelected = prev.includes(rowValue)
                    ? prev.filter(p => p !== rowValue) // Remove if already selected
                    : [...prev, rowValue]; // Add if not selected
                
                // Use setTimeout to ensure this runs after the current render cycle
                setTimeout(() => {
                    onSelectedProducersChange?.(newSelected);
                }, 0);
                
                return newSelected;
            });
        } else if (mainViewSelected === 'destination' || mainViewSelected === 'market') {
            setSelectedDestinations(prev => {
                const newSelected = prev.includes(rowValue)
                    ? prev.filter(p => p !== rowValue) // Remove if already selected
                    : [...prev, rowValue]; // Add if not selected
                
                // Use setTimeout to ensure this runs after the current render cycle
                setTimeout(() => {
                    onSelectedDestinationsChange?.(newSelected);
                }, 0);
                
                return newSelected;
            });
        }
    }, [onSelectedProducersChange, onSelectedDestinationsChange]);

    // Get row class name based on selection state
    const getRowClassName = (rowValue: string) => {
        const baseClass = 'mtdSummary-producerList';
        // Determine which selection array to check based on current view
        const currentSelections = mainViewSelected === 'producer' ? selectedProducers : selectedDestinations;
        const hasSelections = currentSelections.length > 0;
        const isSelected = currentSelections.includes(rowValue);
        
        if (hasSelections) {
            return isSelected 
                ? `${baseClass} selected` 
                : `${baseClass} unselected`;
        }
        
        return baseClass;
    };

    // Calculate dynamic grid column amount/width
    const gridTemplateColumns = useMemo(() => {
        const dataColumns = AGGREGATION_COLUMNS.map(config => {
            if (config.width) {
                return `minmax(${config.width}, max-content)`;
            }
            return 'minmax(80px, max-content)';
        }).join(' ');
        
        return `minmax(150px, auto) ${dataColumns}`;
    }, [AGGREGATION_COLUMNS]);

    return (
        <div>

            <div 
                className='mtdSummary-producerListHeader'
                style={{ gridTemplateColumns: gridTemplateColumns }}
            >
                <div>{mainViewSelected === 'producer' ? 'Producer' : mainViewSelected === 'destination' ? 'Destination' : 'Market'}</div>
                {AGGREGATION_COLUMNS.map(config => (
                    <div 
                        key={config.key as string}
                        style={{ textAlign: config.headerAlignment || config.alignment || 'left' }}
                    >
                        {config.label}
                    </div>
                ))}
            </div>

            <div className='mtdSummary-dividingLine'/>

            {/* Data rows */}
            {listSummaryData.map(({ groupedData, aggregations }) => (
                <div
                    key={groupedData}
                    className={getRowClassName(groupedData)}
                    onClick={() => handleSummaryClick(groupedData)}
                    style={{ gridTemplateColumns: gridTemplateColumns }}
                >
                    <div>{groupedData}</div>
                    {AGGREGATION_COLUMNS.map(config => {
                        const value = aggregations[config.key as string] || 0;
                        const formattedValue = config.formatting ? formatValue(value, config.formatting) : value.toString();
                        
                        return (
                            <div 
                                key={config.key as string}
                                style={{ textAlign: config.alignment || 'left', paddingLeft: '5px' }}
                            >
                                {formattedValue}
                            </div>
                        );
                    })}
                </div>
            ))}

            <div className='mtdSummary-dividingLine'/>

            {/* Subtotal */}
            <div 
                className='mtdSummary-producerListSubtotal'
                style={{ gridTemplateColumns: gridTemplateColumns }}
            > 
                <div style={{ fontWeight: 'bold' }}>Subtotal</div>
                {AGGREGATION_COLUMNS.map(config => {
                    const value = (subtotals && subtotals[config.key as string]) || 0;
                    const formattedValue = config.formatting ? formatValue(value, config.formatting) : value.toString();
                    
                    return (
                        <div 
                            key={config.key as string}
                            style={{ 
                                textAlign: config.alignment || 'left',
                                fontWeight: 'bold',
                                paddingLeft: '5px'
                            }}
                        >
                            {formattedValue}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SummaryList;